import {
  NotificationItemViewModel,
  type NotificationGroupType,
} from './NotificationItemViewModel'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { type NotificationStore } from '@/features/notifications/NotificationStore'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import { ViewModelList } from '@/infra/view-model-list'
import { distinctUntilChanged, filter, map, merge, switchMap } from 'rxjs'
import { type Notification } from '@/features/notifications/Notification'
import { computed, action, observable } from 'mobx'
import { task } from 'mobx-task'
import { isEqual, orderBy } from 'lodash-es'
import type { JobFn } from '@/features/jobs'
import { type Job } from '@/features/jobs/Job'
import { tapAction, toStream } from '@/utils/mobx-util'
import type { GetTimeFn } from '@/features/notifications'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { NotificationThreadViewModel } from './NotificationThreadViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { resolveOnAbort, throwIfAborted } from '@/utils/abort-util'

/**
 * Notification list view model.
 */
export class NotificationListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private _initialFetch: Promise<unknown> | null = null
  private _hasLoaded = false

  // Not pruned, so that a thread created again shows the job it already
  // received.
  @observable.shallow private accessor _jobs = new Map<
    string,
    Job | undefined
  >()

  private readonly _viewModels: ViewModelList<
    Notification,
    NotificationItemViewModel
  >

  private readonly _threadVms: ViewModelList<
    string,
    NotificationThreadViewModel
  >

  /**
   * Notifications scroller.
   */
  readonly scroller = new InfiniteScrollProvider({
    limit: 100,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Unable to load notifications',
        signal,
      )

      return withRetry(() =>
        this._notificationStore.listNotifications(
          { ...paging, workspaceId: this.workspace.id || undefined },
          signal,
        ),
      )
    },
  })

  constructor(
    readonly workspace: Workspace,
    private readonly _notificationStore: NotificationStore,
    private readonly _getTime: GetTimeFn,
    private readonly _onInteract: (notification: Notification) => unknown,
    private readonly _job: JobFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._viewModels = new ViewModelList({
      models: () => this.scroller.items.slice(),
      create: (notification) =>
        new NotificationItemViewModel(
          notification,
          this._getTime,
          this.interact,
        ),
    })
    this._threadVms = new ViewModelList({
      models: () => [...this._threadItems.keys()],
      create: (key) => {
        const jobId = this._threadItems.get(key)![0].jobId
        return new NotificationThreadViewModel(
          key,
          jobId,
          () => this._threadItems.get(key) ?? [],
          () => (jobId ? this._jobs.get(jobId) : undefined),
        )
      },
    })
    this._lifecycle.manage(this._viewModels).manage(this._threadVms)
    this._lifecycle.manage(
      toStream(() => this.jobIds, true).pipe(
        distinctUntilChanged<string[]>(isEqual),
        switchMap((jobIds) =>
          merge(
            ...jobIds.map((jobId) =>
              this._job(jobId).pipe(map((job) => [jobId, job] as const)),
            ),
          ),
        ),
        tapAction(([jobId, job]) => {
          this._jobs.set(jobId, job)
        }),
      ),
    )

    // Always keep the scroller in sync since lists are cached per workspace
    // and stay loaded between activations.
    syncInfiniteScrollerItems({
      scroller: this.scroller,
      additions$: _notificationStore.notificationUpdated$.pipe(
        filter(
          (notification) =>
            workspace === Workspace.none ||
            notification.recipient.workspace === workspace,
        ),
      ),
    }).subscribe()
  }

  /**
   * Notifications, newest first.
   */
  @computed
  get items(): NotificationItemViewModel[] {
    return orderBy(this._viewModels.items, 'dispatchedAt', 'desc')
  }

  /**
   * Number of unread notifications.
   */
  @computed
  get unreadCount() {
    return this.items.filter((item) => !item.isRead).length
  }

  /**
   * Notifications threaded by the job they're about, ordered by each thread's
   * newest notification. Notifications without a job stand alone.
   */
  @computed
  get threads(): NotificationThreadViewModel[] {
    return this._threadVms.items
  }

  /**
   * Jobs the notifications are about, in list order.
   */
  @computed
  get jobIds(): string[] {
    return this.threads
      .map((thread) => thread.jobId)
      .filter((id): id is string => id !== null)
  }

  /**
   * The items of each thread, newest first, keyed by job or, for a
   * notification without one, by the notification.
   */
  @computed
  private get _threadItems(): Map<string, NotificationItemViewModel[]> {
    const threads = new Map<string, NotificationItemViewModel[]>()
    for (const item of this.items) {
      const key = item.jobId ?? `notification:${item.id}`
      const existing = threads.get(key)
      if (existing) {
        existing.push(item)
      } else {
        threads.set(key, [item])
      }
    }
    return threads
  }

  /**
   * Groups of notifications.
   */
  @computed
  get notificationGroups(): NotificationGroup[] {
    const today: NotificationGroup = { type: 'Today', notifications: [] }
    const older: NotificationGroup = { type: 'Older', notifications: [] }
    this.items.forEach((item) => {
      const group = item.notificationGroupType === 'Today' ? today : older
      group.notifications.push(item)
    })

    return [today, older].filter((g) => g.notifications.length > 0)
  }

  /**
   * Whether the notifications are refetching while the previous ones stay on
   * screen.
   */
  @computed
  get isRevalidating() {
    return this.scroller.isRevalidating
  }

  /**
   * Whether the first notifications are still loading.
   */
  @computed
  get isFetchingInitial() {
    return this.fetch.pending && !this.isRevalidating
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The list renders a failed load from the task's state.
    this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the first page of notifications.
   */
  fetch = task(async (signal: AbortSignal) => {
    if (this._hasLoaded) {
      await this.scroller.fetch({ signal, staleWhileRevalidate: true })
      return
    }

    // Only the wait is aborted; the first page is shared with the unread
    // count, so its fetch carries on.
    throwIfAborted(await resolveOnAbort(this.fetchNotifications(), signal))
    this._hasLoaded = true
  })

  /**
   * Fetches the first page of notifications.
   * NOTE: This is memoized; the unread count and every activation share the
   * first fetch, and a failed fetch is retried on the next call.
   */
  fetchNotifications() {
    this._initialFetch ??= this.scroller.fetch().catch((err: unknown) => {
      this._initialFetch = null
      throw err
    })
    return this._initialFetch
  }

  /**
   * Marks the notification as read and notifies the host
   * of this view model of the interaction.
   *
   * @param notification
   */
  @action.bound
  interact(notification: Notification) {
    this._notificationStore
      .markAsRead(notification)
      .catch(this._createAlertErrorHandler('Marking as read failed'))
      .catch(() => undefined)
    this._onInteract(notification)
  }

  /**
   * Mark all notifications as read.
   */
  markAllAsRead = task.resolved(async () => {
    await this._notificationStore
      .markAllAsRead(this.workspace)
      .catch(this._createAlertErrorHandler('Marking all as read failed'))
  })
}

/**
 * Notification group.
 */
export interface NotificationGroup {
  type: NotificationGroupType
  notifications: NotificationItemViewModel[]
}
